import React from "react"

import { UpdateUsersInGroupOperation } from "@common-shared/config/practice.config"
import {
    IOneVerifyBillerGroup,
    IOneVerifyOrganizationUser,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"

import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { billerGroupsSelector } from "../../../store/selectors/billerGroups.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { billerGroupsSelectUser } from "../../../store/slices/billerGroups.slice"
import {
    billerGroupsGetSelected,
    billerGroupsUpdateUsers,
} from "../../../store/thunks/billerGroups.thunks"

import closeIcon from "@front-shared/assets/images/icons/close-dark.svg"
import trashIcon from "@front-shared/assets/images/icons/trash-dark2.svg"

export const BillerGroupUsersInformationComponent: React.FunctionComponent<
    IBillerGroupUsersInformationComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const billerGroups = useAppSelector(billerGroupsSelector)

    const onDeselectUser = () => {
        dispatch(billerGroupsSelectUser())
    }

    const onRemoveUserFromGroup = (group: IOneVerifyBillerGroup) => {
        if (!selectedPracticeRole) {
            return
        }

        ConfirmationHelper.enableConfirmation(
            () => {
                dispatch(
                    billerGroupsUpdateUsers({
                        payload: {
                            billerGroupId: group.id,
                            data: {
                                userIds: [props.user.id],
                                operation: UpdateUsersInGroupOperation.REMOVE,
                            },
                        },

                        practice: selectedPracticeRole.practice,

                        onSuccess: () => {
                            // Deselect user on success - if group that was removed from user is active one
                            // Otherwise reload active group

                            if (
                                !billerGroups.selectedItem?.id ||
                                !selectedPracticeRole
                            ) {
                                return
                            }

                            if (group.id === billerGroups.selectedItem.id) {
                                onDeselectUser()
                            } else {
                                dispatch(
                                    billerGroupsGetSelected({
                                        payload: {
                                            billerGroupId:
                                                billerGroups.selectedItem.id,
                                            ignoreSelectedUserReset: true,
                                        },
                                        practice: selectedPracticeRole.practice,
                                        ignorePreloader: true,
                                    }),
                                )
                            }
                        },
                    }),
                )
            },
            undefined,
            `User "${
                props.user.name || "No name"
            }" will be permanently removed from group`,
        )
    }

    return (
        <div
            className={`
                relative bg-brand-warmLight05 h-full overflow-y-auto
                md:fixed md:top-0 md:left-0 md:w-screen md:h-screen md:bg-brand-white md:z-20
                ${props.className}
            `}
        >
            <div className="bg-brand-white w-full p-[24px]">
                <PrimaryText typography="subtitleSemibold">
                    {props.user.name || "No Name"}
                </PrimaryText>

                <div className="mt-[16px]">
                    {props.user.billerGroups.map((group, index) => (
                        <div
                            key={index}
                            className="
                                py-[12px] flex items-center
                                border-t border-solid border-brand-warmLight
                            "
                        >
                            <PrimaryText className="flex-1 mr-[16px]">
                                {group.name}
                            </PrimaryText>

                            <div
                                className="flex items-center cursor-pointer"
                                onClick={() => {
                                    onRemoveUserFromGroup(group)
                                }}
                            >
                                <PrimaryText
                                    typography="textSemibold"
                                    className="mr-[16px]"
                                >
                                    Remove
                                </PrimaryText>

                                <img src={trashIcon} alt="Remove" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div
                className="absolute cursor-pointer z-10 top-0 right-0 px-[24px] py-[20px]"
                onClick={onDeselectUser}
            >
                <img src={closeIcon} alt="Close" />
            </div>
        </div>
    )
}

interface IBillerGroupUsersInformationComponentProps {
    className?: string
    user: IOneVerifyOrganizationUser
}
